import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { Location, Navigate, useNavigate } from 'react-router';
import { Link, useLocation } from 'react-router-dom';
import * as yup from 'yup';

import { AuthShell } from '@/components/auth/AuthShell';
import { ContinueWithProviderButtons } from '@/components/common/ContinueWithProviderButtons';
import { Button, Field, IconButton, TextInput } from '@/components/ds';
import { useAuthContext } from '@/context/AuthProvider';
import { useUserRole } from '@/hooks/useUserRole';
import { useTranslationPrefix } from '@/translations/i18n';
import { isUserProfileIncomplete } from '@/utils/user';
import { validatePassword } from '@/utils/validators';

export interface LoginLocationState {
  from?: Location;
  email?: string;
}

const Login: React.FC = () => {
  const { user, logIn, completeNewPassword, isLoggingIn, logOut } = useAuthContext();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const location = useLocation() as Location<LoginLocationState | undefined>;
  const redirectTo = location.state?.from?.pathname || '/dashboard';
  const initialEmail = location.state?.email;
  const t = useTranslationPrefix('login');
  const validationT = useTranslationPrefix('validation');
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  const loginValidationSchema = yup.object({
    email: yup
      .string()
      .email(validationT('valid', { name: t('email') }))
      .required(validationT('required', { name: t('email') })),
    password: yup
      .string()
      .min(8, validationT('min_string', { name: t('password'), min: 8 }))
      .required(validationT('required', { name: t('password') })),
  });

  const newPasswordValidationSchema = yup.object({
    newPassword: yup
      .string()
      .min(8, validationT('min_string', { name: t('new_password.new_password'), min: 8 }))
      .required(validationT('required', { name: t('new_password.new_password') }))
      .test('isValidPassword', validationT('password'), validatePassword),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('newPassword')], validationT('passwords_do_not_match'))
      .required(validationT('required', { name: t('new_password.confirm_password') })),
  });

  const loginFormik = useFormik({
    initialValues: {
      email: initialEmail || '',
      password: '',
    },
    enableReinitialize: true,
    validationSchema: loginValidationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        setError(null);
        const result = await logIn(values.email, values.password);
        if (result === 'NEW_PASSWORD_REQUIRED') {
          setNeedsNewPassword(true);
        }
      } catch (error: any) {
        setError(error.message);
      }
    },
  });

  const newPasswordFormik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: newPasswordValidationSchema,
    validateOnBlur: false,
    onSubmit: async (values) => {
      try {
        setError(null);
        await completeNewPassword(values.newPassword, { name: loginFormik.values.email });
      } catch (error: any) {
        setError(error.message);
      }
    },
  });

  const onTogglePasswordClick = () => setShowPassword((show) => !show);
  const onToggleNewPasswordClick = () => setShowNewPassword((show) => !show);
  const onToggleConfirmPasswordClick = () => setShowConfirmPassword((show) => !show);

  // handle OAuth authentication redirect and errors
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');

    if (errorParam && errorParam === 'already_logged_in') {
      logOut().then(() => {
        navigate('/login');
      });
    } else if (errorParam) {
      setError(errorDescription || `Authentication error: ${errorParam}`);
    }
  }, [navigate, logOut]);

  if (user) {
    // Only staff are gated on profile completion here — self-serve buyers and
    // advisor-managed buyers go straight to /dashboard, where DashboardRedirect
    // (and, for self-serve owners, the onboarding modal on home-hub) handles it.
    if ((isAdvisor || canAccessAdmin) && isUserProfileIncomplete(user)) {
      return <Navigate to="/user/update-profile" replace state={{ hideTopNav: true }} />;
    }
    return <Navigate to={redirectTo} replace />;
  }

  // a password field: the house text input with its show/hide toggle at the end
  const passwordInput = (props: {
    name: string;
    value: string;
    show: boolean;
    onToggle: () => void;
    toggleLabel: string;
    autoComplete: string;
    placeholder: string;
    autoFocus?: boolean;
    testId: string;
    toggleTestId: string;
    error?: string | false;
    onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  }) => (
    <TextInput
      type={props.show ? 'text' : 'password'}
      name={props.name}
      required
      placeholder={props.placeholder}
      autoComplete={props.autoComplete}
      autoFocus={props.autoFocus}
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      error={Boolean(props.error)}
      inputProps={{ 'aria-label': props.placeholder, 'data-testid': props.testId }}
      endAdornment={
        <IconButton
          icon={props.show ? 'eye-off' : 'eye'}
          label={props.toggleLabel}
          size="sm"
          variant="ghost"
          tipPlacement={false}
          onClick={props.onToggle}
          data-testid={props.toggleTestId}
        />
      }
      sx={{ pr: '4px' }}
    />
  );
  const fieldError = (msg?: string | false) => (msg ? <span className="hx-auth-field-error">{msg}</span> : null);

  return (
    <AuthShell
      title={needsNewPassword ? t('new_password.title') : 'Sign in'}
      footer={
        !needsNewPassword && (
          <>
            {t('create_account.0')}
            <Link className="hx-auth-link" to="/register" data-testid="login-register-link">
              {t('create_account.1')}
            </Link>
          </>
        )
      }>
      <style>{`.hx-auth-field-error { font-size: 11.5px; font-weight: 500; color: #DB5282; }`}</style>
      {!needsNewPassword ? (
        <form onSubmit={loginFormik.handleSubmit} data-testid="login-form">
          <h1 className="hx-auth-title">Welcome back</h1>
          <p className="hx-auth-sub">Sign in to pick up where you left off.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label={t('email')} htmlFor="login-email">
              <TextInput
                id="login-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                value={loginFormik.values.email}
                onChange={loginFormik.handleChange}
                onBlur={loginFormik.handleBlur}
                error={loginFormik.touched.email && Boolean(loginFormik.errors.email)}
                inputProps={{ 'data-testid': 'login-email-input' }}
              />
              {fieldError(loginFormik.touched.email && loginFormik.errors.email)}
            </Field>
            <Field
              label={t('password')}
              aside={
                <Link
                  className="hx-auth-link"
                  to="/password-reset/request"
                  style={{ fontSize: 12, fontWeight: 500, color: 'rgba(247,247,246,0.6)' }}
                  data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              }>
              {passwordInput({
                name: 'password',
                value: loginFormik.values.password,
                show: showPassword,
                onToggle: onTogglePasswordClick,
                toggleLabel: t('toggle_password'),
                autoComplete: 'current-password',
                placeholder: t('password'),
                testId: 'login-password-input',
                toggleTestId: 'login-toggle-password-btn',
                error: loginFormik.touched.password && loginFormik.errors.password,
                onChange: loginFormik.handleChange,
                onBlur: loginFormik.handleBlur,
              })}
              {fieldError(loginFormik.touched.password && loginFormik.errors.password)}
            </Field>
            {error && (
              <p className="hx-auth-error" data-testid="login-error-message">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" block disabled={isLoggingIn} data-testid="login-submit-btn">
              {isLoggingIn ? 'Signing in…' : 'Sign in'}
            </Button>
            {/* single sign-on under the form, a quiet tray button, so lime Sign in stays the one primary action */}
            <ContinueWithProviderButtons onError={setError} />
          </div>
        </form>
      ) : (
        <form onSubmit={newPasswordFormik.handleSubmit} data-testid="new-password-form">
          <h1 className="hx-auth-title">{t('new_password.title')}</h1>
          <p className="hx-auth-sub">{t('new_password.description')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="New password">
              {passwordInput({
                name: 'newPassword',
                value: newPasswordFormik.values.newPassword,
                show: showNewPassword,
                onToggle: onToggleNewPasswordClick,
                toggleLabel: t('new_password.toggle_new_password'),
                autoComplete: 'new-password',
                placeholder: t('new_password.new_password'),
                autoFocus: true,
                testId: 'new-password-input',
                toggleTestId: 'new-password-toggle-btn',
                error: newPasswordFormik.touched.newPassword && newPasswordFormik.errors.newPassword,
                onChange: newPasswordFormik.handleChange,
                onBlur: newPasswordFormik.handleBlur,
              })}
              {fieldError(newPasswordFormik.touched.newPassword && newPasswordFormik.errors.newPassword)}
            </Field>
            <Field label="Confirm password">
              {passwordInput({
                name: 'confirmPassword',
                value: newPasswordFormik.values.confirmPassword,
                show: showConfirmPassword,
                onToggle: onToggleConfirmPasswordClick,
                toggleLabel: t('new_password.toggle_confirm_password'),
                autoComplete: 'new-password',
                placeholder: t('new_password.confirm_password'),
                testId: 'confirm-password-input',
                toggleTestId: 'confirm-password-toggle-btn',
                error: newPasswordFormik.touched.confirmPassword && newPasswordFormik.errors.confirmPassword,
                onChange: newPasswordFormik.handleChange,
                onBlur: newPasswordFormik.handleBlur,
              })}
              {fieldError(newPasswordFormik.touched.confirmPassword && newPasswordFormik.errors.confirmPassword)}
            </Field>
            {error && (
              <p className="hx-auth-error" data-testid="new-password-error-message">
                {error}
              </p>
            )}
            <Button type="submit" variant="primary" block disabled={isLoggingIn} data-testid="new-password-submit-btn">
              {t('new_password.submit')}
            </Button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default Login;
